/**
 * Stale-chunk recovery for `React.lazy` dynamic imports.
 *
 * When the app is redeployed, the index HTML references new hashed asset
 * filenames (e.g. `PatientResourcePage-y6Ukj2WG.js`). Any browser tab still
 * running the previous build references the old hashes; if the user triggers a
 * lazy route *after* the deploy, the request 404s and Vite throws
 * `TypeError: Failed to fetch dynamically imported module: ...`.
 *
 * This module detects that specific failure mode and recovers gracefully:
 *
 *   1. First failure → retry the dynamic import once. Transient network blips
 *      are recovered without a reload.
 *   2. Retry also fails → force a one-time full page reload so the browser
 *      fetches the fresh `index.html` and the new asset manifest.
 *   3. A session-storage guard (`CHUNK_RELOAD_STORAGE_KEY`) prevents an
 *      infinite reload loop if the post-reload build is *still* broken.
 *
 * Other error types (e.g. actual exceptions inside the loaded module) are
 * re-thrown untouched so the route `errorElement` can render the usual
 * fallback UI.
 *
 * @agentic connections
 */

import { lazy } from "react";
import { reportError } from "@/app/boot/initializeDatadog";
import type { ComponentType } from "react";

/** SessionStorage key used to avoid infinite reload loops. @agentic connections */
export const CHUNK_RELOAD_STORAGE_KEY = "benchmark:chunk-reload-attempted";

/**
 * Build a cache-busted retry URL from a chunk-load error message when one is available.
 * @agentic connections
 */
export const getChunkRetryUrl = (
  error: unknown,
  timestamp: number = Date.now(),
): string | null => {
  if (typeof window === "undefined") return null;
  if (!error || typeof error !== "object") return null;

  const err = error as { message?: unknown };
  const message = typeof err.message === "string" ? err.message : "";
  const match = message.match(
    /(?:Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed):\s*(\S+)/,
  );

  if (!match) return null;

  try {
    const retryUrl = new URL(match[1], window.location.href);
    retryUrl.searchParams.set("t", String(timestamp));
    return retryUrl.toString();
  } catch {
    return null;
  }
};

/**
 * Heuristically detect a stale-chunk / dynamic-import failure across browsers
 * and bundlers. Covers Vite, webpack, Chromium, Firefox, and Safari wording.
 * @agentic connections
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const err = error as { name?: unknown; message?: unknown };
  const name = typeof err.name === "string" ? err.name : "";
  const message = typeof err.message === "string" ? err.message : "";

  if (name === "ChunkLoadError") return true;

  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("Loading chunk") ||
    message.includes("Loading CSS chunk")
  );
};

/** Options for `wrapImportWithRetry`. Test seams only. @agentic connections */
type WrapImportWithRetryOptions<T> = {
  reload?: () => void;
  retryImport?: (error: unknown) => Promise<T>;
};

/**
 * Core retry logic, extracted from `lazyWithRetry` so it can be unit-tested
 * without mounting a React tree.
 * @agentic connections
 */
export const wrapImportWithRetry = async <T>(
  importer: () => Promise<T>,
  options: WrapImportWithRetryOptions<T> = {},
): Promise<T> => {
  const reload =
    options.reload ??
    (() => {
      if (typeof window !== "undefined") window.location.reload();
    });
  const retryImport =
    options.retryImport ??
    ((error: unknown): Promise<T> => {
      const retryUrl = getChunkRetryUrl(error);
      if (!retryUrl) return importer();
      return import(/* @vite-ignore */ retryUrl) as Promise<T>;
    });

  try {
    const result = await importer();
    // A successful import confirms the current build's chunks are reachable —
    // clear the reload guard so a *future* deploy in the same session can
    // still trigger one-shot recovery.
    if (typeof sessionStorage !== "undefined") {
      try {
        sessionStorage.removeItem(CHUNK_RELOAD_STORAGE_KEY);
      } catch {
        // Ignore storage access errors (private mode, disabled storage).
      }
    }
    return result;
  } catch (firstError) {
    if (!isChunkLoadError(firstError)) throw firstError;

    try {
      return await retryImport(firstError);
    } catch (secondError) {
      if (!isChunkLoadError(secondError)) throw secondError;

      let alreadyReloaded = false;
      if (typeof sessionStorage !== "undefined") {
        try {
          alreadyReloaded =
            sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) === "1";
        } catch {
          // Ignore storage access errors (private mode, disabled storage).
        }
      }

      if (alreadyReloaded) {
        // We already reloaded once this session and chunks are *still*
        // missing. The deploy is likely broken on the server; surface the
        // error to the route `errorElement` rather than reloading forever.
        reportError(secondError, {
          source: "lazyWithRetry",
          outcome: "reload-loop-guard-triggered",
        });
        throw secondError;
      }

      try {
        sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, "1");
      } catch {
        // SessionStorage may be unavailable (private mode / disabled). The
        // reload still happens; we just lose loop protection.
      }

      reportError(secondError, {
        source: "lazyWithRetry",
        outcome: "reloading-for-new-build",
      });

      reload();

      // Swallow the promise: returning/rejecting here would briefly render
      // the route `errorElement` before the browser navigates. A never-
      // resolving promise keeps the Suspense fallback visible during reload.
      return new Promise<T>(() => {
        /* intentionally never settles */
      });
    }
  }
};

/**
 * Drop-in replacement for `React.lazy` that recovers from stale-chunk errors
 * after a redeploy. See file header for the full recovery algorithm.
 *
 * Typed `typeof lazy` so callers get the exact same generic inference as
 * `React.lazy` — no call-site changes needed.
 * @agentic connections
 */
export const lazyWithRetry = ((
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  importer: () => Promise<{ default: ComponentType<any> }>,
) => lazy(() => wrapImportWithRetry(importer))) as typeof lazy;
